import React, { useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColGroupDef, GridApi } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import StatCard from "./StatCard";
import { SecondaryButton } from "../../../components/Button";
import { DetailResponse } from "../shared/useDetailQuery";
import { buildMethodologyCsvRows } from "../shared/metricDefinitions";
import { formatMetric, METRIC_LABELS } from "../shared/format";
import { MetricKey } from "../api/types";

interface DetailTableProps {
  data: DetailResponse;
  columnDefs: (ColDef | ColGroupDef)[];
  // Summary cards drawn from the response totals (in order); [] to hide.
  summaryMetrics: MetricKey[];
  emphasisMetric?: MetricKey;
  exportName: string;
  csvExtra?: string[];
}

// Renders a detail-list response (Transaction / Order Items / Audit): summary cards + a dense,
// sortable/filterable ag-grid + a self-documenting CSV export. Excluded rows are shaded, and the
// Totals always cover the full set even when Rows are capped (Truncated).
export default function DetailTable({
  data,
  columnDefs,
  summaryMetrics,
  emphasisMetric,
  exportName,
  csvExtra,
}: DetailTableProps) {
  const gridRef = useRef<GridApi>();

  const handleExport = () => {
    gridRef.current?.exportDataAsCsv({
      fileName: `${exportName}_${data.window.from}_to_${data.window.to}.csv`,
      allColumns: true,
      prependContent: [
        ...buildMethodologyCsvRows(Object.keys(data.totals) as MetricKey[], data.window),
        ...(csvExtra ?? []).map((line) => [{ data: { value: line } }]),
        [
          {
            data: {
              value: `Rows exported: ${data.rowCount}${
                data.truncated ? " (TRUNCATED — totals still cover the full set)" : ""
              }`,
            },
          },
        ],
        [{ data: { value: "" } }],
      ],
    });
  };

  return (
    <>
      {summaryMetrics.length > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {summaryMetrics
            .filter((m) => m in data.totals)
            .map((m) => (
              <StatCard
                key={m}
                label={METRIC_LABELS[m]}
                value={formatMetric(m, data.totals[m])}
                emphasis={m === emphasisMetric}
              />
            ))}
        </div>
      )}

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-600">
          {data.rowCount.toLocaleString()} rows
          {data.truncated && (
            <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              TRUNCATED — raise the row limit or narrow the filters; totals still cover the full set.
            </span>
          )}
        </div>
        <SecondaryButton onClick={handleExport}>Export to CSV</SecondaryButton>
      </div>

      <div style={{ height: "62vh" }}>
        <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
          <AgGridReact
            rowData={data.rows as unknown[]}
            columnDefs={columnDefs as ColDef[]}
            defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 90 }}
            rowHeight={36}
            pagination
            paginationPageSize={100}
            enableCellTextSelection
            getRowStyle={(p) =>
              p.data && (p.data as { included?: boolean }).included === false
                ? { backgroundColor: "#fef2f2", color: "#9ca3af" }
                : undefined
            }
            onGridReady={(p) => (gridRef.current = p.api)}
          />
        </div>
      </div>
    </>
  );
}
